import Button from "common/Buttons/Button";
import {
  ONBOARDING_TELEGRAM_CODE_ID,
  ONBOARDING_TELEGRAM_CONNECT_ID,
} from "testIds";
import React, { useState } from "react";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import LoadingOutlined from "@ant-design/icons/LoadingOutlined";
import { useTheme } from "styled-components";
import TelegramUserPreview from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/TelegramUserPreview";
import Text from "common/Typography/Text";
import { useSelector } from "react-redux";
import { buttonStyles } from "common/Buttons/Button";
import SubmitButton from "features/onboarding/common/SubmitButton";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import styled from "styled-components";
import ConnectExternalAccountFooter from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccountFooter";
import { UUID_ALREADY_CONNECTED_WARNING_MESSAGE } from "@shared/features/onboarding/constants";
import {
  ExternalAccountType,
  GetBasicDetailsFromUuidData,
} from "@shared/features/onboarding/types";
import GetUuidModal from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/GetUuidModal";
import { OnboardingStyles } from "features/onboarding/common/styles";
import IntercomLink from "common/Text/IntercomLink";
import { UUID_ALREADY_CONNECTED_ARTICLE_ID } from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface ConnectTelegramProps {
  uuid: string;
  setUuid: (uuid: string) => void;
  getBasicDetailsFromUuidData: GetBasicDetailsFromUuidData;
  onContinueClick?: () => void;
}

const ConnectTelegram = ({
  uuid,
  setUuid,
  getBasicDetailsFromUuidData,
  onContinueClick,
}: ConnectTelegramProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const [getUuidModalOpen, setGetUuidModalOpen] = useState(false);
  const appTheme = useTheme();

  let isSuccess = false;
  let statusIcon = <span />;
  const iconFontSize = 18;
  if (getBasicDetailsFromUuidData.isFetching) {
    statusIcon = (
      <LoadingOutlined
        style={{ fontSize: iconFontSize, color: appTheme.colors.secondaryGrey }}
        spin
      />
    );
  } else if (getBasicDetailsFromUuidData.isError) {
    statusIcon = (
      <CloseOutlined
        style={{ fontSize: iconFontSize, color: appTheme.colors.red }}
      />
    );
  } else if (getBasicDetailsFromUuidData.data) {
    isSuccess = true;
    statusIcon = (
      <CheckOutlined
        style={{ fontSize: iconFontSize, color: appTheme.colors.green }}
      />
    );
  }

  const inputSuffix = <Flex $alignItems="center">{statusIcon}</Flex>;

  const handleContinueClick = () => {
    if (getBasicDetailsFromUuidData?.isAlreadyRegisteredToAppError) {
      getBasicDetailsFromUuidData.showUuidInUseMessage();
    } else {
      onContinueClick?.();
    }
  };

  let inputDescription;
  if (getBasicDetailsFromUuidData.isInvalidUuid) {
    inputDescription = (
      <OnboardingStyles.InputDescription
        type="danger"
        style={{ marginBottom: 0 }}
      >
        {t("external_account.telegram.invalid_uuid")}
      </OnboardingStyles.InputDescription>
    );
  } else if (getBasicDetailsFromUuidData.isAlreadyRegisteredToAppError) {
    inputDescription = (
      <OnboardingStyles.InputDescription
        style={{ marginBottom: 0 }}
        type="danger"
      >
        {t(UUID_ALREADY_CONNECTED_WARNING_MESSAGE)}
        <IntercomLink
          articleId={UUID_ALREADY_CONNECTED_ARTICLE_ID}
          style={{ marginLeft: 4, fontSize: typography.fontSizes.S }}
        />
      </OnboardingStyles.InputDescription>
    );
  }

  const showUserPreview = !!getBasicDetailsFromUuidData.data;

  const submitButton = (
    <StyledSubmitButton
      data-testid={ONBOARDING_TELEGRAM_CONNECT_ID}
      onClick={handleContinueClick}
      disabled={!getBasicDetailsFromUuidData.data}
      $isUuidConnected={
        getBasicDetailsFromUuidData?.isAlreadyRegisteredToAppError
      }
      $showUserPreview={showUserPreview}
    >
      {t("external_account.telegram.connect_button")}
    </StyledSubmitButton>
  );

  return (
    <>
      <Text style={{ fontWeight: 600 }}>{t("revision.telegram_step1")}</Text>
      <Text type="medium">{t("revision.telegram_instruction")}</Text>
      <Button
        type="primary"
        size="large"
        onClick={() => setGetUuidModalOpen(true)}
        style={{ width: "100%", margin: "16px 0 24px" }}
      >
        {t("external_account.telegram.get_uuid")}
      </Button>
      <Text style={{ fontWeight: 600, marginBottom: 8 }}>
        {t("revision.telegram_step2")}
      </Text>
      <Text style={{ marginBottom: 8 }}>
        {t("external_account.telegram.uuid_label")}
      </Text>
      <OnboardingStyles.Input
        data-testid={ONBOARDING_TELEGRAM_CODE_ID}
        aria-label={t("external_account.telegram.uuid_label")}
        value={uuid}
        placeholder={t("external_account.telegram.uuid_placeholder")}
        onChange={(e) => setUuid(e.target.value)}
        suffix={inputSuffix}
        style={{ flex: "auto" }}
      />
      {inputDescription}
      {showUserPreview && (
        <TelegramUserPreview
          telegramUser={getBasicDetailsFromUuidData.data}
          isLoading={getBasicDetailsFromUuidData.isFetching}
        />
      )}
      <ConnectExternalAccountFooter
        submitButton={submitButton}
        externalAccountType={ExternalAccountType.TELEGRAM}
      />
      <GetUuidModal
        isVisible={getUuidModalOpen}
        onCancel={() => setGetUuidModalOpen(false)}
        botUrl={user?.bot_url}
      />
    </>
  );
};

export default ConnectTelegram;

const StyledSubmitButton = styled(SubmitButton)<{
  $isUuidConnected: boolean;
  $showUserPreview: boolean;
}>`
  ${(p) =>
    p.$isUuidConnected &&
    `
    ${buttonStyles.antBtnDisabled};
    cursor: auto;
    `}
  margin-top: ${(p) => (p.$showUserPreview ? 0 : "24px")};
`;
